import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import { Box, Stack } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { STATUS_BADGE } from "../../../theme/tokens";
import type { TrackerButtonState } from "./trackedPlan.utils";

type Props = {
	// How many sliders carry a committable value — the "Add all" count; 0 disables it.
	valuedCount: number;
	onAddAll: () => void;
	// How many levers are in the Action Plan; 0 disables tracking.
	planSize: number;
	trackerState: TrackerButtonState;
	onTrackPlan: () => void;
	isTrackingPlan: boolean;
};

const TRACKER_ICON: Record<TrackerButtonState, typeof CheckCircleIcon> = {
	tracked: CheckCircleIcon,
	switch: SwapHorizIcon,
	untracked: TrackChangesIcon,
};

const TRACKER_LABEL_KEY: Record<TrackerButtonState, string> = {
	tracked: "pbj_model.tracker.state_tracked",
	switch: "pbj_model.tracker.state_switch",
	untracked: "pbj_model.tracker.state_add",
};

/**
 * The recommendations panel footer: commit the current slider positions to the Action Plan in one
 * click ("Add all"), and beside it commit the WHOLE assembled plan to the tracker.
 */
export const RecommendationsFooter: FC<Props> = ({ valuedCount, onAddAll, planSize, trackerState, onTrackPlan, isTrackingPlan }) => {
	const { t } = useTranslation();
	const TrackerIcon = TRACKER_ICON[trackerState];
	return (
		<Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", bgcolor: "background.default" }}>
			<SystemButton
				buttonVariant="primary"
				size="small"
				disabled={valuedCount === 0}
				onClick={onAddAll}
				leadingIcon={<PlaylistAddCheckIcon sx={{ fontSize: 16 }} />}
				sx={{ width: "100%" }}
				data-testid="pbj-model-recommendations-add-all"
			>
				{t("pbj_model.recommendations.add_all", { count: valuedCount })}
			</SystemButton>
			{/* "Tracked" is a settled confirmation, not a re-pressable button — it renders disabled with a
			    check so the affordance matches the meaning. */}
			<InHouseTooltip arrow title={planSize === 0 ? t("pbj_model.tracker.empty_plan_tooltip") : ""}>
				{/* The state attribute rides the wrapper: SystemButton drops unknown `data-*` props. */}
				<Box sx={{ width: "100%" }} data-tracker-state={trackerState}>
					<SystemButton
						buttonVariant={trackerState === "tracked" ? "secondary" : "primary"}
						size="small"
						disabled={planSize === 0 || trackerState === "tracked" || isTrackingPlan}
						loading={isTrackingPlan}
						onClick={onTrackPlan}
						leadingIcon={<TrackerIcon sx={{ fontSize: 16 }} />}
						sx={{
							width: "100%",
							// The settled state reads as a success confirmation, so it keeps its ink while disabled —
							// otherwise "Tracked" greys out and looks like it failed.
							...(trackerState === "tracked"
								? {
										color: STATUS_BADGE.SUCCESS_INK,
										borderColor: STATUS_BADGE.SUCCESS_INK,
										"&.Mui-disabled": { color: STATUS_BADGE.SUCCESS_INK, borderColor: STATUS_BADGE.SUCCESS_BG },
									}
								: {}),
						}}
						data-testid="pbj-model-recommendations-track-plan"
					>
						{t(TRACKER_LABEL_KEY[trackerState])}
					</SystemButton>
				</Box>
			</InHouseTooltip>
		</Stack>
	);
};
